import { ReactNode } from 'react';
import { BaseFilters, ServiceReturn } from '@/models/Service';
import { ClassNameComponent } from '@/utils/ClassNameComponent';

export type FieldValues = Record<string, FieldValue>;

export type FieldValue =
  | string
  | number
  | boolean
  | Array<string | number>
  | object
  | undefined;

export type BaseFilterProps = {
  label: string;
  hide?: boolean;
  className?: ClassNameComponent;
  icon?: ReactNode;
  hideClear?: boolean;
};

export type OmitLabelProps = Omit<BaseFilterProps, 'label'>;

export type FilterOption<FieldValue> = { label: string; value: FieldValue };

export type StaticOptionsProps<FieldValue> = {
  mode: 'static';
  options: FilterOption<FieldValue>[];
  service?: undefined;
  withSearch?: undefined;
};

export type BooleanFilterProps = BaseFilterProps & {
  name: string;
  type: 'boolean';
  value?: boolean;
};

export type MultipleBooleanFilterProps = BaseFilterProps & {
  type: 'multipleBoolean';
  options: BooleanFilterProps[];
  values?: Array<string | number>;
};

export type DynamicOptionsProps<
  Model,
  FieldValue,
  Filter extends BaseFilters = BaseFilters,
> = {
  mode: 'dynamic';
  service: (filters?: Filter) => ServiceReturn<Model>;
  makeOptions: (options: Model[]) => FilterOption<FieldValue>[];
  filters?: Filter;
  withSearch?: boolean;
};

export type MultipleFilterProps<
  Model,
  FieldValue,
  Filter extends BaseFilters = BaseFilters,
> = BaseFilterProps & {
  name: string;
  type: 'multiple';
  values?: FieldValue[];
} & (
    | StaticOptionsProps<FieldValue>
    | DynamicOptionsProps<Model, FieldValue, Filter>
  );

export type SingleFilterProps<
  Model,
  FieldValue,
  Filter extends BaseFilters = BaseFilters,
> = BaseFilterProps & {
  name: string;
  type: 'single';
  value?: FieldValue;
} & (
    | StaticOptionsProps<FieldValue>
    | DynamicOptionsProps<Model, FieldValue, Filter>
  ) & {
    label?: string;
  };

export type SelectFilterProps<Value = FieldValue> = Omit<
  BaseFilterProps,
  'label'
> & {
  name: string;
  type: 'select';
  label?: string;
  value?: Value;
  options: FilterOption<Value>[];
  showOnClear?: boolean;
};

export type SearchFilterProps = Omit<BaseFilterProps, 'label'> & {
  name: string;
  type: 'search';
  placeholder?: string;
  debounce?: boolean;
  value?: string;
  label?: string;
  className?: ClassNameComponent;
};

export type OrderingFilterProps<Value = FieldValue> = OmitLabelProps & {
  label?: string;
  name: string;
  value?: Value;
  options: [FilterOption<Value>, FilterOption<Value>];
};

export type DualArrowOrderingFilterProps<Value = FieldValue> =
  OrderingFilterProps<Value> & {
    type: 'dualArrowOrdering';
  };

export type TextArrowOrderingFilterProps<Value = FieldValue> =
  OrderingFilterProps<Value> & {
    type: 'textArrowOrdering';
    hideClear?: boolean;
  };

export type ButtonFilterProps<Value = FieldValue> = BaseFilterProps & {
  name: string;
  value?: Value;
  type: 'button';
  active?: boolean;
  badge?: number;
};

export type ObjectSelectFilterProps<Value = FieldValue> = BaseFilterProps & {
  type: 'selectComposite';
  value?: Record<string, Value>;
  options: FilterOption<Record<string, Value>>[];
  onChange?: (selected: Record<string, Value>) => void;
};

export type FilterProps<
  Model = any,
  Value = FieldValue,
  Filter extends BaseFilters = BaseFilters,
> =
  | BooleanFilterProps
  | SearchFilterProps
  | MultipleBooleanFilterProps
  | DualArrowOrderingFilterProps<Value>
  | TextArrowOrderingFilterProps<Value>
  | SelectFilterProps<Value>
  | ButtonFilterProps<Value>
  | ObjectSelectFilterProps<Value>
  | MultipleFilterProps<Model, Value, Filter>
  | SingleFilterProps<Model, Value, Filter>;

export function isDynamicFilter<Model, FieldValue, Filter extends BaseFilters>(
  props:
    | StaticOptionsProps<FieldValue>
    | DynamicOptionsProps<Model, FieldValue, Filter>,
): props is DynamicOptionsProps<Model, FieldValue, Filter> {
  return props.mode === 'dynamic';
}

export type FilterFormProps = {
  setValue(name: string, value?: FieldValue): void;
  setValues?(object: Partial<FieldValues>): void;
  onClear?: () => void;
};
